import axios from "axios";
import React, { useEffect } from "react";
import { Table, Button, Form, Card } from "react-bootstrap";

const TAX_RATE = 0.025; // 2.5%

const HotelBillEntry = ({ bill, setBill }) => {
  const items = bill?.items || [];

  /* ---------------- TAX CALC ---------------- */
  const calculateTaxes = (charges) => {
    const value = parseFloat(charges || 0);

    const cgst = value * TAX_RATE;
    const sgst = value * TAX_RATE;

    return {
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      total: (value + cgst + sgst).toFixed(2),
    };
  };

  /* ---------------- UPDATE ITEM ---------------- */
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === "charges") {
      const taxes = calculateTaxes(value);
      updated[index].cgst = taxes.cgst;
      updated[index].sgst = taxes.sgst;
      updated[index].total = taxes.total;
    }

    setBill({ ...bill, items: updated });
  };

  /* ---------------- ADD / REMOVE ---------------- */
  const addRow = () => {
    setBill((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        {
          date: "",
          description: "",
          charges: "",
          credit: "",
          cgst: "0.00",
          sgst: "0.00",
          total: "0.00",
        },
      ],
    }));
  };

  const removeRow = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setBill({ ...bill, items: updated });
  };

  /* ---------------- TOTAL ---------------- */
  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const rowTotal = Number(item.total || 0);
      const credit = Number(item.credit || 0);
      return sum + rowTotal - credit;
    }, 0);
  };

  const total = calculateTotal();

  /* ---------------- INPUT GUARD ---------------- */
  const numericGuard = (value, callback) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      callback(value);
    }
  };

  /* ---------------- AUTO ADD ROW FOR WALKIN ---------------- */
  useEffect(() => {
    if (
      bill.billType === "walkin" &&
      (!bill.items || bill.items.length === 0)
    ) {
      addRow();
    }
  }, [bill.billType]);

  return (
    <Card className="p-3 no-print">
      <h5 className="mb-3">Invoice Entry</h5>

      {/* ---------------- HEADER ---------------- */}
      <div className="row mb-3">
        {/* BILL TYPE */}
        <div className="col-md-4 mb-2">
          <Form.Label>Bill Type</Form.Label>
          <Form.Select
            value={bill.billType || "customer"}
            onChange={(e) =>
              setBill({ ...bill, billType: e.target.value })
            }
          >
            <option value="customer">Customer (Room)</option>
            <option value="walkin">Walk-in (Food / Snacks)</option>
          </Form.Select>
        </div>

        {/* DATE */}
        <div className="col-md-4 mb-2">
          <Form.Label>Date</Form.Label>
          <Form.Control
            type="date"
            value={bill.invoiceDate || ""}
            onChange={(e) =>
              setBill({ ...bill, invoiceDate: e.target.value })
            }
          />
        </div>

        {/* INVOICE NO */}
        <div className="col-md-4 mb-2">
          <Form.Label>Invoice No</Form.Label>
          <Form.Control
            value={bill.invoiceNo || ""}
            onChange={(e) =>
              setBill({ ...bill, invoiceNo: e.target.value })
            }
          />
        </div>

        {/* PAYMENT */}
        <div className="col-md-4 mb-2">
          <Form.Label>Mode of Payment</Form.Label>
          <Form.Select
            value={bill.paymentMode || ""}
            onChange={(e) =>
              setBill({ ...bill, paymentMode: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="Cash">Cash</option>
            <option value="UPI">UPI</option>
            <option value="Card">Card</option>
            <option value="Bank Transfer">Bank Transfer</option>
          </Form.Select>
        </div>

        {/* CONDITIONAL HOTEL FIELDS */}
        {bill.billType !== "walkin" && (
          <>
            <div className="col-md-3 mb-2">
              <Form.Label>Check In</Form.Label>
              <Form.Control
                type="date"
                value={bill.checkIn || ""}
                onChange={(e) =>
                  setBill({ ...bill, checkIn: e.target.value })
                }
              />
            </div>

            <div className="col-md-3 mb-2">
              <Form.Label>Check Out</Form.Label>
              <Form.Control
                type="date"
                value={bill.checkOut || ""}
                onChange={(e) =>
                  setBill({ ...bill, checkOut: e.target.value })
                }
              />
            </div>

            <div className="col-md-3 mb-2">
              <Form.Label>Room</Form.Label>
              <Form.Control
                value={bill.room || ""}
                onChange={(e) =>
                  setBill({ ...bill, room: e.target.value })
                }
              />
            </div>
          </>
        )}

        {/* CUSTOMER NAME */}
        <div className="col-md-6 mb-2">
          <Form.Label>
            {bill.billType === "walkin"
              ? "Customer Name (Optional)"
              : "Bill To"}
          </Form.Label>
          <Form.Control
            value={bill.billTo || ""}
            onChange={(e) =>
              setBill({ ...bill, billTo: e.target.value })
            }
          />
        </div>

        {/* PHONE */}
        <div className="col-md-6 mb-2">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            value={bill.phone || ""}
            onChange={(e) =>
              setBill({ ...bill, phone: e.target.value })
            }
          />
        </div>

        {/* GST */}
        <div className="col-md-6 mb-2">
          <Form.Label>GSTIN</Form.Label>
          <Form.Control
            value={bill.gstin || ""}
            onChange={(e) =>
              setBill({ ...bill, gstin: e.target.value })
            }
          />
        </div>
      </div>

      {/* ---------------- ITEMS ---------------- */}
      <Table bordered size="sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Charges</th>
            <th>CGST</th>
            <th>SGST</th>
            <th>Credit</th>
            <th>Total</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td>
                <Form.Control
                  type="date"
                  value={item.date}
                  onChange={(e) =>
                    updateItem(idx, "date", e.target.value)
                  }
                />
              </td>

              <td>
                <Form.Control
                  value={item.description}
                  placeholder={
                    bill.billType === "walkin"
                      ? "Tea / Coffee / Snacks"
                      : "Room Rent / Food / Laundry"
                  }
                  onChange={(e) =>
                    updateItem(idx, "description", e.target.value)
                  }
                />
              </td>

              <td>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  value={item.charges}
                  onChange={(e) =>
                    numericGuard(e.target.value, (val) =>
                      updateItem(idx, "charges", val)
                    )
                  }
                />
              </td>

              <td className="text-end">{item.cgst}</td>
              <td className="text-end">{item.sgst}</td>

              <td>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  value={item.credit}
                  onChange={(e) =>
                    numericGuard(e.target.value, (val) =>
                      updateItem(idx, "credit", val)
                    )
                  }
                />
              </td>

              <td className="text-end">{item.total}</td>

              <td>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => removeRow(idx)}
                >
                  ✕
                </Button>
              </td>
            </tr>
          ))}

          <tr>
            <td colSpan={5}></td>
            <td className="text-end">
              <strong>Grand Total</strong>
            </td>
            <td className="text-end">
              <strong>{total.toFixed(2)}</strong>
            </td>
            <td></td>
          </tr>
        </tbody>
      </Table>

      <Button onClick={addRow}>Add Row</Button>
    </Card>
  );
};

export default HotelBillEntry;